import { createLink, useNavigate, useRouterState } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import { Briefcase, Calendar, ChevronDown, Eye, Filter, Home, Newspaper, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from '~/components/shadcn/navigation-menu.tsx';
import { ScrollArea } from '~/components/shadcn/scroll-area.tsx';
import { useTranslations } from '~/i18n/use-translations.ts';
import { useKeyboardShortcut, useShowKeyboardNavigationHint } from '~/modules/keyboard/hooks.ts';
import { KEYBOARD_SHORTCUTS } from '~/modules/keyboard/settings.ts';
import type { AppShellData } from '~/server/app/get-app-shell-data.ts';
import { cn } from '~/utils/cn.ts';
import { defer } from '~/utils/run-immediately.ts';

import { Notifications } from '../main-navbar/notifications/index.tsx';
import { ThemeSwitch } from '../main-navbar/theme-switch.tsx';
import { CommandBadge } from './navigation-command-palette/command-badge.tsx';
import { NavigationCommandPalette } from './navigation-command-palette.tsx';

interface MainNavbarProps {
	readonly isMobile: boolean;
	readonly shellData: AppShellData;
}

interface NavLinkItemProps {
	readonly alwaysShowLabel?: boolean;
	readonly command?: string[];
	readonly icon?: LucideIcon;
	readonly isActive: boolean;
	readonly isDialogOpen: boolean;
	readonly isMobile?: boolean;
	readonly label: string;
	readonly showKeyboardNavigation?: boolean;
	readonly to?: string;
	readonly unavailable?: boolean;
}

interface NavDropdownItemProps {
	readonly children?: React.ReactNode;
	readonly command?: string[];
	readonly icon?: LucideIcon;
	readonly isDialogOpen: boolean;
	readonly label: string;
	readonly onOpenChange?: (isOpen: boolean) => void;
	readonly showKeyboardNavigation?: boolean;
}

const NavigationMenuRouterLink = createLink(NavigationMenuLink);
const mobileHeaderActionClassName = cn(
	'relative inline-flex size-11 shrink-0 items-center justify-center text-foreground',
	"before:pointer-events-none before:absolute before:inset-1.5 before:border before:border-border/70 before:bg-transparent before:transition-colors before:content-['']",
	'hover:before:border-border hover:before:bg-accent focus-visible:outline-none focus-visible:before:border-ring focus-visible:before:bg-accent focus-visible:before:ring-2 focus-visible:before:ring-ring',
);

function CompanyWordmark({ isDialogOpen }: { readonly isDialogOpen: boolean }) {
	return (
		<div
			className={cn(
				'group/nav flex h-6 w-full items-center justify-start gap-0 rounded-md py-4 pr-2 pl-2 text-foreground transition-[gap] delay-150 duration-300',
				'group-focus-within:gap-3 group-focus-within:delay-0 group-hover:gap-3 group-hover:delay-0',
			)}
		>
			<svg
				className="shrink-0 text-foreground"
				fill="none"
				height={29}
				viewBox="0 0 18 29"
				width={18}
				xmlns="http://www.w3.org/2000/svg"
			>
				<title>Eqity</title>
				<path
					clipRule="evenodd"
					d="M14.002 4.868c0-.593-.236-1.162-.657-1.582L11.728 1.68l-9.412 9.372a5 5 0 0 0-1.472 3.536 4.98 4.98 0 0 0 1.472 3.536L3.897 19.7a5.96 5.96 0 0 1 1.636-5.462l7.812-7.788c.42-.42.657-.99.657-1.583m-.66 6.17a2.26 2.26 0 0 1 0 3.19L9.39 18.184a6 6 0 0 0-1.773 4.285c0 .415.044.83.128 1.236l-1.558-1.602a5.07 5.07 0 0 1 0-7.16l5.542-5.54zm-.04 11.71c.222.211.398.465.518.744a2.23 2.23 0 0 1 0 1.765c-.12.28-.296.533-.518.745l-1.607 1.565-1.633-1.598a4.94 4.94 0 0 1-1.489-3.286 4.92 4.92 0 0 1 1.156-3.411z"
					fill="currentColor"
					fillOpacity={0.85}
					fillRule="evenodd"
				/>
			</svg>
			{!isDialogOpen ? (
				<span
					className={cn(
						'max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-[max-width,opacity] duration-300',
						'group-focus-within:max-w-[114px] group-focus-within:opacity-100',
						'group-hover:max-w-[114px] group-hover:opacity-100',
					)}
				>
					Eqity
				</span>
			) : null}
		</div>
	);
}

function NavLinkItem({
	alwaysShowLabel = false,
	command,
	icon: Icon,
	isActive,
	isDialogOpen,
	isMobile = false,
	label,
	showKeyboardNavigation,
	to,
	unavailable = false,
}: NavLinkItemProps) {
	const t = useTranslations();
	const navigate = useNavigate();

	useKeyboardShortcut({
		callback:
			to && !unavailable
				? () => {
						void navigate({ to });
					}
				: undefined,
		command,
		shouldFire: Boolean(to && !unavailable),
	});

	const className = cn(
		'group/nav flex h-6 w-full items-center justify-start gap-0 rounded-md py-4 pr-2 pl-2 text-foreground transition-[gap] delay-150 duration-300',
		alwaysShowLabel ? 'pointer-events-auto relative z-10' : '',
		!isMobile ? 'group-focus-within:gap-3 group-focus-within:delay-0 group-hover:gap-3 group-hover:delay-0' : '',
		!isMobile && !unavailable ? 'hover:bg-accent hover:text-muted-foreground focus:bg-accent' : '',
		isActive && !unavailable ? 'bg-accent/70' : '',
		unavailable ? 'cursor-not-allowed opacity-45' : '',
	);

	const labelNode = alwaysShowLabel ? (
		<span className="block truncate text-sm">{label}</span>
	) : !isDialogOpen && !isMobile ? (
		<span
			className={cn(
				'max-w-0 overflow-hidden whitespace-nowrap text-sm opacity-0 transition-[max-width,opacity] duration-300',
				'group-focus-within:max-w-[114px] group-focus-within:opacity-100',
				'group-hover:max-w-[114px] group-hover:opacity-100',
				'group-hover/nav:underline group-focus/nav-within:underline group-focus/nav:underline',
			)}
		>
			{label}
		</span>
	) : null;

	const content = (
		<>
			{Icon ? <Icon className="size-4 shrink-0 text-foreground" /> : null}
			{labelNode}
			{showKeyboardNavigation ? <CommandBadge command={command} /> : null}
		</>
	);

	if (to && !unavailable) {
		return (
			<NavigationMenuItem asChild>
				<NavigationMenuRouterLink
					className={cn(className, isMobile && 'w-8')}
					preload="intent"
					title={t('nav.navigateTo', { destination: label })}
					to={to}
				>
					{content}
				</NavigationMenuRouterLink>
			</NavigationMenuItem>
		);
	}

	return <div className={cn(className, isMobile && 'w-8')}>{content}</div>;
}

function NavDropdownItem({
	children,
	command,
	icon: Icon,
	isDialogOpen,
	label,
	onOpenChange,
	showKeyboardNavigation,
}: NavDropdownItemProps) {
	const [isOpen, setIsOpen] = useState(false);

	function handleOpenChange(nextValue: boolean) {
		setIsOpen(nextValue);
		onOpenChange?.(nextValue);
	}

	return (
		<NavigationMenuItem
			className="pointer-events-none w-full"
			onMouseEnter={() => handleOpenChange(true)}
			onMouseLeave={() => handleOpenChange(false)}
		>
			<NavigationMenuTrigger
				className="pointer-events-auto flex w-full flex-row px-0"
				hasDropdownArrow={false}
				onClick={() => handleOpenChange(!isOpen)}
			>
				<span
					className={cn(
						'group/nav flex h-6 w-full items-center justify-start gap-0 rounded-md py-4 pr-2 pl-2 text-foreground transition-[gap] delay-150 duration-300',
						'hover:bg-accent hover:text-muted-foreground focus:bg-accent',
						'group-focus-within:gap-3 group-focus-within:delay-0 group-hover:gap-3 group-hover:delay-0',
					)}
				>
					{Icon ? <Icon className="size-4 shrink-0 text-foreground" /> : null}
					{!isDialogOpen ? (
						<div
							className={cn(
								'flex flex-row items-center text-sm',
								'max-w-0 overflow-hidden whitespace-nowrap',
								'opacity-0 transition-[max-width,opacity] duration-300',
								'group-focus-within:max-w-[114px] group-focus-within:opacity-100',
								'group-hover:max-w-[114px] group-hover:opacity-100',
							)}
						>
							<span>{label}</span>
							<span
								className={cn(
									'mt-[2px] ml-auto',
									'transition-transform group-data-[state=closed]/nav:rotate-0 group-data-[state=open]/nav:rotate-180',
								)}
							>
								<ChevronDown className="size-4 text-foreground" />
							</span>
						</div>
					) : null}
					{showKeyboardNavigation ? <CommandBadge command={command} /> : null}
				</span>
			</NavigationMenuTrigger>
			<NavigationMenuContent
				active={isOpen && !isDialogOpen}
				className="pointer-events-auto relative z-60 bg-card"
				onMouseEnter={() => handleOpenChange(true)}
				onMouseLeave={() => handleOpenChange(false)}
				transition="accordion"
			>
				<ul className="my-2 ml-4 grid w-full border-border border-l pl-4">{children}</ul>
			</NavigationMenuContent>
		</NavigationMenuItem>
	);
}

function DesktopNavbar({ shellData }: Pick<MainNavbarProps, 'shellData'>) {
	const t = useTranslations();
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const hiddenFocusRef = useRef<HTMLButtonElement>(null);
	const showKeyboardHint = useShowKeyboardNavigationHint();
	const [isNotificationOpen, setIsNotificationOpen] = useState(false);
	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const [isThemeOpen, setIsThemeOpen] = useState(false);
	const [isInlineDropdownOpen, setIsInlineDropdownOpen] = useState(false);
	const isDialogOpen = isSearchOpen || isNotificationOpen;
	const showKeyboardNavigation = showKeyboardHint && !isDialogOpen;
	const customStyles = cn(
		'group/nav flex w-full items-center justify-start py-4 pr-2 pl-2',
		'h-6 gap-0 rounded-md text-foreground',
		'transition-[gap] delay-150 duration-300',
		'hover:bg-accent',
		'focus:bg-accent',
		'hover:text-muted-foreground',
		'group-focus-within:gap-3 group-focus-within:delay-0 group-hover:gap-3 group-hover:delay-0',
	);
	const textWidth = cn(
		'max-w-0 overflow-hidden whitespace-nowrap',
		'transition-[max-width,opacity] duration-300',
		'group-focus-within:max-w-[114px] group-hover:max-w-[114px]',
	);

	useEffect(() => {
		if (!pathname) {
			return;
		}

		defer(() => hiddenFocusRef.current?.focus());
	}, [pathname]);

	return (
		<>
			<button className="pointer-events-none absolute h-0 opacity-0" ref={hiddenFocusRef} tabIndex={-1} type="button" />
			<ScrollArea
				classNames={{
					root: cn('hidden h-dvh overflow-x-hidden sm:block', isInlineDropdownOpen ? 'w-sidebar-open-width' : 'w-fit'),
					viewport: cn('h-full', isInlineDropdownOpen ? 'w-sidebar-open-width' : 'w-fit'),
				}}
				orientation="vertical"
				styles={{
					viewport: {
						position: 'fixed',
						zIndex: 'var(--z-index-navbar)',
					},
				}}
			>
				<NavigationMenu className="group h-full" orientation="vertical">
					<NavigationMenuList
						className={cn(
							'group flex list-none flex-col items-start bg-card py-4 shadow-lg transition-all duration-300',
							'min-h-dvh w-sidebar-width',
							isInlineDropdownOpen ? 'w-sidebar-open-width' : '',
							!isDialogOpen
								? cn('z-30', 'group-focus-within:w-sidebar-open-width', 'group-hover:w-sidebar-open-width')
								: 'z-10',
						)}
					>
						<ul className="flex w-full flex-col">
							<ul className="flex w-full flex-col items-start border-border border-b px-3 pb-4">
								<CompanyWordmark isDialogOpen={isDialogOpen} />
							</ul>
							<ul className="flex w-full flex-col items-start px-3 pt-4">
								<NavigationCommandPalette
									command={KEYBOARD_SHORTCUTS.search}
									isDialogOpen={isDialogOpen}
									isOpen={isSearchOpen}
									latestSearches={shellData.latestSearches}
									searchSuggestions={shellData.searchSuggestions}
									setIsOpen={setIsSearchOpen}
									showKeyboardNavigation={showKeyboardNavigation}
								/>
							</ul>
						</ul>

						<ul className="mt-[15vh] flex w-full flex-col items-start gap-y-4 px-3">
							<NavLinkItem
								command={KEYBOARD_SHORTCUTS.home}
								icon={Home}
								isActive={pathname === '/dashboard'}
								isDialogOpen={isDialogOpen}
								label={t('nav.dashboard')}
								showKeyboardNavigation={showKeyboardNavigation}
								to="/dashboard"
							/>
							<NavDropdownItem
								command={KEYBOARD_SHORTCUTS.portfolios}
								icon={Briefcase}
								isDialogOpen={isDialogOpen}
								label={t('nav.portfolios')}
								onOpenChange={setIsInlineDropdownOpen}
								showKeyboardNavigation={showKeyboardNavigation}
							>
								{shellData.portfolios?.map((portfolio, index) => {
									const shortcut = [...KEYBOARD_SHORTCUTS.portfolios, index.toString()];

									return (
										<NavLinkItem
											alwaysShowLabel
											command={shortcut}
											isActive={
												pathname === `/portfolios/${portfolio.id}` ||
												pathname.startsWith(`/portfolios/${portfolio.id}/`)
											}
											isDialogOpen={isDialogOpen}
											key={portfolio.id}
											label={portfolio.name}
											showKeyboardNavigation={showKeyboardNavigation}
											to={`/portfolios/${portfolio.id}`}
										/>
									);
								})}
							</NavDropdownItem>
							<NavDropdownItem
								command={KEYBOARD_SHORTCUTS.watchlists}
								icon={Eye}
								isDialogOpen={isDialogOpen}
								label={t('nav.watchlists')}
								onOpenChange={setIsInlineDropdownOpen}
								showKeyboardNavigation={showKeyboardNavigation}
							>
								{shellData.watchlists?.map((watchlist, index) => {
									const shortcut = [...KEYBOARD_SHORTCUTS.watchlists, index.toString()];

									return (
										<NavLinkItem
											alwaysShowLabel
											command={shortcut}
											isActive={pathname === `/watchlists/${index}/overview`}
											isDialogOpen={isDialogOpen}
											key={watchlist.id}
											label={watchlist.name}
											showKeyboardNavigation={showKeyboardNavigation}
											to={`/watchlists/${index}/overview`}
										/>
									);
								})}
							</NavDropdownItem>
							<NavLinkItem
								command={KEYBOARD_SHORTCUTS.news}
								icon={Newspaper}
								isActive={pathname.startsWith('/news')}
								isDialogOpen={isDialogOpen}
								label={t('nav.news')}
								showKeyboardNavigation={showKeyboardNavigation}
								to="/news"
							/>
							<NavLinkItem
								command={KEYBOARD_SHORTCUTS.calendar}
								icon={Calendar}
								isActive={pathname.startsWith('/calendar')}
								isDialogOpen={isDialogOpen}
								label={t('nav.calendar')}
								showKeyboardNavigation={showKeyboardNavigation}
								to="/calendar"
							/>
							<NavLinkItem
								command={KEYBOARD_SHORTCUTS.filter}
								icon={Filter}
								isActive={pathname.startsWith('/screener')}
								isDialogOpen={isDialogOpen}
								label={t('nav.screener')}
								showKeyboardNavigation={showKeyboardNavigation}
								to="/screener"
							/>
						</ul>

						<ul className="mt-auto flex w-full flex-col items-start gap-y-3 px-3 pb-2">
							<NavigationMenuItem asChild>
								<ThemeSwitch
									className={customStyles}
									command={KEYBOARD_SHORTCUTS.theme}
									isDialogOpen={isDialogOpen}
									isOpen={isThemeOpen}
									setIsOpen={setIsThemeOpen}
									showKeyboardNavigation={showKeyboardNavigation}
									textWidth={textWidth}
								/>
							</NavigationMenuItem>
							<NavigationMenuItem asChild>
								<Notifications
									className={customStyles}
									command={KEYBOARD_SHORTCUTS.notifications}
									isDialogOpen={isDialogOpen}
									isOpen={isNotificationOpen}
									setIsOpen={setIsNotificationOpen}
									showKeyboardNavigation={showKeyboardNavigation}
									textWidth={textWidth}
								/>
							</NavigationMenuItem>
							<NavLinkItem
								command={KEYBOARD_SHORTCUTS.user}
								icon={User}
								isActive={pathname.startsWith('/user')}
								isDialogOpen={isDialogOpen}
								label={t('nav.user')}
								showKeyboardNavigation={showKeyboardNavigation}
								to="/user"
							/>
						</ul>
					</NavigationMenuList>
					<div className="pointer-events-none absolute inset-y-0 right-[-1px] w-1 bg-linear-to-r from-transparent to-accent/10" />
				</NavigationMenu>
			</ScrollArea>
		</>
	);
}

function MobileNavbar({ shellData }: Pick<MainNavbarProps, 'shellData'>) {
	const [isNotificationOpen, setIsNotificationOpen] = useState(false);
	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const isDialogOpen = isSearchOpen || isNotificationOpen;

	return (
		<NavigationMenu className="fixed top-0 right-0 left-0 z-50 h-sidebar-height">
			<NavigationMenuList
				className={cn(
					'flex h-full w-body-width justify-between bg-card px-1.5 align-center shadow-md',
					'h-sidebar-height',
				)}
			>
				<ul className="flex w-full flex-row items-center justify-start">
					<NavigationCommandPalette
						command={KEYBOARD_SHORTCUTS.search}
						isDialogOpen={isDialogOpen}
						isMobile
						isOpen={isSearchOpen}
						latestSearches={shellData.latestSearches}
						searchSuggestions={shellData.searchSuggestions}
						setIsOpen={setIsSearchOpen}
						triggerClassName={mobileHeaderActionClassName}
					/>
				</ul>
				<ul className="flex w-full flex-row items-center justify-end gap-x-2">
					<Notifications
						className={mobileHeaderActionClassName}
						command={KEYBOARD_SHORTCUTS.notifications}
						isDialogOpen={isDialogOpen}
						isMobile
						isOpen={isNotificationOpen}
						setIsOpen={setIsNotificationOpen}
						textWidth="max-w-0 overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-300"
					/>
				</ul>
			</NavigationMenuList>
		</NavigationMenu>
	);
}

export function MainNavbar({ isMobile, shellData }: MainNavbarProps) {
	if (isMobile) {
		return <MobileNavbar shellData={shellData} />;
	}

	return <DesktopNavbar shellData={shellData} />;
}
